import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import { Grid } from '@mui/material';
import React, { useContext, useMemo, useState } from 'react';
import { translate } from '../../../../../types/translation/Translator';
import { CreateTaskInput, Task, TaskType } from '../../../../../types/task';
import { BinContext } from '../../../../../context/BinContext';
import { ProductMasterDataContext } from '../../../../../context/ProductMasterDataContext';
import { ProductContext } from '../../../../../context/ProductContext';
import ToggleButton from '../../../Common/ToggleButton';
import Selector from '../../../Common/Selector';
import { testIds } from '../../../../../util/identifiers/identifiers.util';
import ModalPane from '../../../../../VentoryUI/components/common/Modal/components/ModalPane';
import { BackButtonTemplate } from '../../../../../VentoryUI/components/common/Button/Templates/BackButton';
import { NextButtonTemplate } from '../../../../../VentoryUI/components/common/Button/Templates/NextButton/NextButton';
import { ContainerContext } from '../../../../../context/ContainerContext';
import { LotContext } from '../../../../../context/LotContext';
import { KitContext } from '../../../../../context/KitContext';

interface CreateTaskEntityPaneInputProps {
  task: CreateTaskInput;
  setTask: (task: Task) => void;
  next: () => void;
  back: () => void;
}

enum TaskEntityView {
  bin = 'bin',
  product = 'product',
  lot = 'lot',
  container = 'container',
  serial = 'serial',
  kit = 'kit',
}

function taskEntityViewToLocalizedString(view: string) {
  switch (view) {
    case TaskEntityView.bin:
      return translate({ key: TranslationKey.bin });
    case TaskEntityView.container:
      return translate({ key: TranslationKey.container });
    case TaskEntityView.product:
      return translate({ key: TranslationKey.product });
    case TaskEntityView.lot:
      return translate({ key: TranslationKey.lot });
    case TaskEntityView.serial:
      return translate({ key: TranslationKey.serial });
    case TaskEntityView.kit:
      return translate({ key: TranslationKey.kit });
  }

  return '';
}

export function CreateTaskEntityPane({ task, setTask, next, back }: CreateTaskEntityPaneInputProps) {
  const { bins } = useContext(BinContext);
  const { containers } = useContext(ContainerContext);
  const { lots } = useContext(LotContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { products } = useContext(ProductContext);
  const { kits } = useContext(KitContext);

  const selectedView = () => {
    if (task.productMasterDataIds().length) return TaskEntityView.product;
    if (task.lotIds().length) return TaskEntityView.lot;
    if (task.containerIds().length) return TaskEntityView.container;
    if (task.serials().length) return TaskEntityView.serial;
    return TaskEntityView.bin;
  };

  const [view, setView] = useState<TaskEntityView>(selectedView());

  const { binsWithProducts, lotsWithProducts, containersWithProducts, serialsWithProducts, kitsWithProducts } =
    useMemo(() => {
      const binsWithProducts = new Set<string>();
      const lotsWithProducts = new Set<string>();
      const containersWithProducts = new Set<string>();
      const serialsWithProducts = new Set<string>();
      const kitsWithProducts = new Set<string>();

      [...products.values()]
        .flatMap(v => [...v.values()])
        .forEach(p => {
          if (p.stockLocationId !== task.stockLocationId) return;
          binsWithProducts.add(p.binId);
          if (p.lotId) lotsWithProducts.add(p.lotId);
          if (p.containerId) containersWithProducts.add(p.containerId);
          if (p.serial) serialsWithProducts.add(p.serial);
          if (p.kitId) kitsWithProducts.add(p.kitId);
        });

      return { binsWithProducts, lotsWithProducts, containersWithProducts, serialsWithProducts, kitsWithProducts };
    }, [products, bins, lots, containers, kits]);

  const serials = useMemo(() => {
    return new Set([
      ...[...products.values()]
        .flatMap(productMap =>
          [...productMap.values()].filter(p => p.companyId === task.companyId).map(product => product.serial),
        )
        .filter(v => !!v),
    ]);
  }, [products]);

  const selectView = () => {
    switch (view) {
      case TaskEntityView.bin:
        return (
          <Selector
            testId={testIds.bins}
            key={'binItems'}
            placeholder={translate({ key: TranslationKey.filterBins })}
            values={[...bins.values()]
              .filter(bin => {
                return (
                  binsWithProducts.has(bin.id) ||
                  (task.type === TaskType.blind && task.stockLocationId === bin.stockLocationId)
                );
              })
              .map(bin => bin.id)}
            checkedValues={task.binIds()}
            toText={item => bins.get(item)?.name || translate({ key: TranslationKey.unknownBin })}
            onChange={checked => setTask(task.withBinIds(checked))}
          />
        );
      case TaskEntityView.product:
        return (
          <Selector<string>
            testId={testIds.products}
            key={'productItems'}
            placeholder={translate({ key: TranslationKey.filterProducts })}
            values={[...productMasterData.values()].filter(p => p.companyId === task.companyId).map(p => p.id)}
            checkedValues={task.productMasterDataIds()}
            toText={item =>
              productMasterData.get(item)?.productName || translate({ key: TranslationKey.unknownProduct })
            }
            onChange={checked => setTask(task.withProductMasterDataIds(checked))}
          />
        );
      case TaskEntityView.container:
        return (
          <Selector
            testId={testIds.containers}
            key={'containers'}
            placeholder={translate({ key: TranslationKey.filterContainer })}
            values={[...containers.values()]
              .filter(c => c.stockLocationId === task.stockLocationId)
              .map(container => container.id)}
            checkedValues={task.containerIds()}
            toText={item => containers.get(item)?.identifier || translate({ key: TranslationKey.unknownContainer })}
            onChange={checked => setTask(task.withContainerIds(checked))}
          />
        );
      case TaskEntityView.lot:
        return (
          <Selector
            testId={testIds.lots}
            key={'lots'}
            placeholder={translate({ key: TranslationKey.filterLot })}
            values={[...lots.values()]
              .filter(lot => {
                if (lot.companyId !== task.companyId) return false;
                return lotsWithProducts.has(lot.id) || task.type === TaskType.blind;
              })
              .map(lot => lot.id)}
            checkedValues={task.lotIds()}
            toText={item => lots.get(item)?.number || translate({ key: TranslationKey.unknownLot })}
            onChange={checked => setTask(task.withLotIds(checked))}
          />
        );
      case TaskEntityView.serial:
        return (
          <Selector<string>
            testId={testIds.serial}
            key={'serial'}
            placeholder={translate({ key: TranslationKey.filterSerials })}
            values={[...serials.values()]
              .filter(serial => {
                return (serial && serialsWithProducts.has(serial)) || task.type === TaskType.blind;
              })
              .map(serial => serial || translate({ key: TranslationKey.unknownSerial }))}
            checkedValues={task.serials()}
            toText={item => item || translate({ key: TranslationKey.unknownSerial })}
            onChange={checked => setTask(task.withSerials(checked))}
          />
        );
      case TaskEntityView.kit:
        return (
          <Selector<string>
            key={'kit'}
            placeholder={translate({ key: TranslationKey.filter })}
            values={[...kits.values()]
              .filter(kit => {
                if (kit.companyId !== task.companyId) return false;
                return kitsWithProducts.has(kit.id) || task.type === TaskType.blind;
              })
              .map(kit => kit.id)}
            checkedValues={task.kits()}
            toText={item => kits.get(item)?.identifier || translate({ key: TranslationKey.unknown })}
            onChange={checked => setTask(task.withKits(checked))}
          />
        );

      default:
        return '';
    }
  };

  return (
    <ModalPane
      overflow='auto'
      testId={testIds.createTaskEntityPane}
      footerButtons={[BackButtonTemplate(back), NextButtonTemplate(next, { disabled: !task.identifiers })]}
    >
      <Grid container height={'100%'} alignContent={'space-between'}>
        <Grid item xs={12}>
          <Grid container rowSpacing={2}>
            <Grid item flexGrow={1} marginY={'auto'}>
              {task.type === TaskType.cycle ? (
                <p className='text-sm font-semibold'>{translate({ key: TranslationKey.selectBinsOrProducts })}</p>
              ) : (
                <p className='text-sm font-semibold'>{translate({ key: TranslationKey.selectBin })}</p>
              )}
            </Grid>
            {task.type === TaskType.cycle ? (
              <Grid item>
                <ToggleButton
                  testId={testIds.entity}
                  onChange={value => {
                    if (task.binIds().length) task.withBinIds([]);
                    if (task.productMasterDataIds().length) task.withProductMasterDataIds([]);
                    if (task.lotIds().length) task.withLotIds([]);
                    if (task.containerIds().length) task.withContainerIds([]);
                    if (task.serials()) task.withSerials([]);
                    if (task.kits()) task.withKits([]);
                    setView(value as TaskEntityView);
                  }}
                  selected={view}
                  values={Object.keys(TaskEntityView).filter(el => {
                    if (el === TaskEntityView.product && task.type === TaskType.blind) return false;
                    return true;
                  })}
                  text={value => taskEntityViewToLocalizedString(value)}
                />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              {selectView()}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </ModalPane>
  );
}
